import React, { useRef, useEffect, useState } from 'react';
import { PlayFill } from 'react-bootstrap-icons';
import analyticsService from '../../services/analyticsService';
import { isPrerendering } from '../../utils/prerendering';
import { useMaxWidth } from '../../hooks/useIsMobile';
import './VideoPlayer.css';

/**
 * Simple VideoPlayer component with autoplay
 *
 * @param {string} src - Video source URL (required)
 * @param {string} poster - Poster/thumbnail image URL (optional)
 * @param {string} alt - Alt text for accessibility
 * @param {string} title - Video title
 * @param {string} videoLabel - Label for analytics (e.g. "bracket-builder-demo"); derived from src if not set
 * @param {boolean} autoplay - Auto-play on load (default: false). If playWhenVisible is true, waits until visible.
 * @param {boolean} playWhenVisible - When true with autoplay, call play() only after the player enters the viewport (default: false)
 * @param {boolean} loop - Loop video (default: false)
 * @param {boolean} muted - Mute video (default: true for autoplay)
 * @param {boolean} inline - Play inline (default: false)
 * @param {boolean} controls - Show video controls (default: true)
 * @param {boolean} revealControlsOnHover - If true, hide controls until hover (fine pointer); always show on touch (default: false)
 * @param {boolean} fill - Fill container with object-fit: cover (default: false)
 * @param {boolean} embedded - Lighter frame for cards/modals; uses object-fit contain when not fill (default: false)
 * @param {boolean} posterPreviewOnMobile - On narrow viewports, show poster + play button until tapped (default: false)
 * @param {boolean} posterPreview - Show poster + centered play button on all viewports until tapped (default: false)
 */
const VideoPlayer = ({
  src,
  poster,
  alt = 'Video',
  title,
  videoLabel,
  analyticsContext,
  autoplay = false,
  playWhenVisible = false,
  loop = false,
  muted = true,
  inline = false,
  controls = true,
  revealControlsOnHover = false,
  fill = false,
  embedded = false,
  posterPreviewOnMobile = false,
  posterPreview = false,
}) => {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const hasTrackedPlay = useRef(false);
  const visibleAutoplayDoneRef = useRef(false);
  const [hovered, setHovered] = useState(false);
  const [posterPreviewDismissed, setPosterPreviewDismissed] = useState(false);
  const isMobileViewport = useMaxWidth(767);
  const [finePointerHover, setFinePointerHover] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches,
  );

  const usePosterPreviewShell =
    Boolean(poster) && (posterPreview || (posterPreviewOnMobile && isMobileViewport));

  useEffect(() => {
    if (!revealControlsOnHover) return undefined;
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    const sync = () => setFinePointerHover(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, [revealControlsOnHover]);

  const showControls = revealControlsOnHover ? !finePointerHover || hovered : controls;

  useEffect(() => {
    visibleAutoplayDoneRef.current = false;
    setPosterPreviewDismissed(false);
  }, [src]);

  useEffect(() => {
    // Skip autoplay during pre-rendering to avoid errors in headless browser
    if (isPrerendering()) return;
    if (!autoplay || playWhenVisible) return;
    if (usePosterPreviewShell && !posterPreviewDismissed) return;

    if (videoRef.current) {
      videoRef.current.play().catch((error) => {
        console.error('Error autoplaying video:', error);
      });
    }
  }, [autoplay, playWhenVisible, usePosterPreviewShell, posterPreviewDismissed, src]);

  useEffect(() => {
    if (isPrerendering()) return;
    if (!autoplay || !playWhenVisible) return;
    if (usePosterPreviewShell && !posterPreviewDismissed) return;

    const container = containerRef.current;
    const video = videoRef.current;
    if (!container || !video) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (visibleAutoplayDoneRef.current) return;
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        visibleAutoplayDoneRef.current = true;
        video.play().catch((error) => {
          console.error('Error autoplaying video when visible:', error);
        });
        observer.disconnect();
      },
      { threshold: 0.2, rootMargin: '0px 0px -6% 0px' }
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, [autoplay, playWhenVisible, usePosterPreviewShell, posterPreviewDismissed, src]);

  // Chrome / iOS fullscreen can reset mute; keep audio off when we intentionally hide controls.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !muted || controls) return undefined;
    const keepMuted = () => {
      const v = videoRef.current;
      if (!v) return;
      if (!v.muted || v.volume > 0) {
        v.muted = true;
        v.volume = 0;
      }
    };
    keepMuted();
    video.addEventListener('play', keepMuted);
    video.addEventListener('playing', keepMuted);
    video.addEventListener('volumechange', keepMuted);
    return () => {
      video.removeEventListener('play', keepMuted);
      video.removeEventListener('playing', keepMuted);
      video.removeEventListener('volumechange', keepMuted);
    };
  }, [muted, controls, src]);

  const shouldAutoplayImmediately =
    autoplay && !playWhenVisible && !(usePosterPreviewShell && !posterPreviewDismissed);

  const handlePlay = () => {
    if (hasTrackedPlay.current) return;
    hasTrackedPlay.current = true;
    const label = videoLabel || (src ? src.replace(/^.*\//, '').replace(/\.[^.]+$/, '') : 'unknown');
    analyticsService.trackTradingEvent('landing_video_started', {
      video_src: src,
      video_label: label,
      page_path: typeof window !== 'undefined' ? window.location.pathname : null,
      ...analyticsContext,
    });
  };

  const handleVideoClick = () => {
    const video = videoRef.current;
    if (!video) return;

    const enterFullscreen = () => {
      try {
        if (video.webkitEnterFullscreen) {
          video.webkitEnterFullscreen();
          return;
        }
        if (video.requestFullscreen) {
          video.requestFullscreen().catch((err) => {
            console.error('Error entering fullscreen:', err);
          });
        } else if (video.webkitRequestFullscreen) {
          video.webkitRequestFullscreen();
        } else if (video.mozRequestFullScreen) {
          video.mozRequestFullScreen();
        } else if (video.msRequestFullscreen) {
          video.msRequestFullscreen();
        }
      } catch (err) {
        console.error('Error entering fullscreen:', err);
      }
    };

    enterFullscreen();

    if (video.paused) {
      video.play().catch((error) => {
        console.error('Error playing video:', error);
      });
    }
  };

  const videoStyle = {
    width: '100%',
    height: fill ? '100%' : 'auto',
    minHeight: fill ? '50vh' : undefined,
    objectFit: fill ? 'cover' : embedded ? 'contain' : undefined,
    objectPosition: fill ? 'right center' : undefined,
    transform: fill ? 'scale(0.92)' : undefined,
    transformOrigin: fill ? 'right center' : undefined,
    display: 'block',
    borderRadius: embedded ? 8 : '15px',
    boxShadow: embedded ? 'none' : '0 15px 35px rgba(0, 0, 0, 0.3)',
    border: 'none',
    outline: 'none',
    cursor: showControls ? 'default' : 'pointer',
    pointerEvents: 'auto',
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation',
  };

  const wrapperStyle = {
    position: 'relative',
    width: usePosterPreviewShell ? 'fit-content' : '100%',
    maxWidth: '100%',
    minHeight: fill ? '50vh' : undefined,
    borderRadius: embedded ? 8 : '15px',
    overflow: 'hidden',
    border: 'none',
    outline: 'none',
    cursor: showControls ? 'default' : 'pointer',
    WebkitTapHighlightColor: 'transparent',
    lineHeight: embedded ? 0 : undefined,
    margin: usePosterPreviewShell ? '0 auto' : undefined,
  };

  const showPosterPreview = usePosterPreviewShell && !posterPreviewDismissed;

  const handlePosterPlay = () => {
    const video = videoRef.current;
    setPosterPreviewDismissed(true);
    if (!video) return;
    video.play().catch((error) => {
      console.error('Error playing video after poster preview:', error);
    });
  };

  const wrapperClassName = showControls ? 'video-player video-player--controls' : 'video-player';

  return (
    <div
      ref={containerRef}
      className={`${wrapperClassName}${usePosterPreviewShell ? ' video-player--poster-preview-shell' : ''}`}
      style={wrapperStyle}
      onClick={showControls || showPosterPreview ? undefined : handleVideoClick}
      onMouseEnter={revealControlsOnHover ? () => setHovered(true) : undefined}
      onMouseLeave={revealControlsOnHover ? () => setHovered(false) : undefined}
    >
      {showPosterPreview && (
        <>
          <img src={poster} alt={alt} className="video-player-poster-image" />
          <button
            type="button"
            className="video-player-poster-play"
            onClick={handlePosterPlay}
            aria-label={`Play ${title || alt || 'video'}`}
          >
            <PlayFill aria-hidden="true" />
          </button>
        </>
      )}
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        loop={loop}
        muted={muted}
        playsInline={true}
        controls={showControls}
        autoPlay={shouldAutoplayImmediately}
        preload={showPosterPreview ? 'none' : 'metadata'}
        onPlay={handlePlay}
        onClick={(e) => {
          if (!showControls) {
            e.preventDefault();
            e.stopPropagation();
            handleVideoClick();
          }
        }}
        onTouchEnd={(e) => {
          if (!showControls) {
            e.preventDefault();
            e.stopPropagation();
            handleVideoClick();
          }
        }}
        style={{
          ...videoStyle,
          display: showPosterPreview ? 'none' : 'block',
        }}
        onMouseEnter={(e) => {
          if (!showControls) {
            e.target.style.cursor = 'pointer';
          }
        }}
      />
    </div>
  );
};

export default VideoPlayer;
